// Thin shim around `@ping/ui`'s `useIncomingConversations` that
// injects the apps/web Dexie adapters. The actual SDK-event →
// persistence logic lives in
// `packages/ui/src/sync/useIncomingConversations.ts`; keeping a
// one-line wrapper here lets the AppShell call site stay identical
// (`useIncomingConversations()` with no args) while the shared hook
// receives the host-specific `ConversationsPersister` + `KVStore`
// instances.
//
// Apps/desktop has its own slice and will mount the same shared hook
// with its own adapters — no behavioural drift between platforms.

import {
  type IncomingNotification,
  useIncomingConversations as sharedUseIncomingConversations,
} from '@ping/ui';

import { getCurrentTransport } from '@session/sessionBootstrap';

import { drainPendingConvState } from './applyConvStatePatch';
import { hydrateMemberProfiles } from './memberProfileHydration';
import { kvAdapter, webConversationsPersister } from './persistenceAdapters';

export function useIncomingConversations(onNotify?: (evt: IncomingNotification) => void): void {
  sharedUseIncomingConversations({
    conversations: webConversationsPersister,
    kv: kvAdapter,
    ...(onNotify !== undefined ? { onNotify } : {}),
    // On every join / group-upgrade / peerAccountId backfill, resolve the
    // roster from the directory so the conversation LIST shows real names +
    // avatars immediately — without waiting for the row to be opened or for a
    // `ping.profile` re-send. Best-effort + idempotent (only writes changes).
    onConversationUpserted: (conversationId) => {
      void hydrateMemberProfiles(conversationId);
      // Replay any cross-device conv_state (delete/archive/pin/mute/favorite/left)
      // that arrived BEFORE this conversation row existed locally — so a chat
      // deleted/archived on another device lands in the right state the instant
      // it materializes here, instead of showing up un-deleted. Best-effort.
      void drainPendingConvState(conversationId);
      // Subscribe this conversation for realtime the INSTANT we join it, instead
      // of waiting up to ~20s for the next `App.tsx` reconcile pass. Without a
      // live per-conversation WS subscription, messages sent right after we join
      // (a peer's immediate reply) are dropped until the reconcile re-subscribes.
      // Idempotent + best-effort (seeds a cursor if absent, re-sends resume).
      void getCurrentTransport()
        ?.subscribeToConversations([conversationId])
        .catch(() => undefined);
    },
  });
}
